import React, { useEffect, useState } from "react";
import { Typography } from "antd";
import dayjs from "dayjs";
import api from "../../../services/axiosInstance";
import GenericReportPage from "../../../components/common/GenericReportPage";
import { fmtMoney, fmtQty } from "../../../utils/reportExport";

const STATUS_OPTIONS = [
    { value: "", label: "All Status" },
    { value: "0", label: "Pending" },
    { value: "1", label: "Approved" },
    { value: "2", label: "Rejected" },
];

const statusLabel = (s) => {
    if (String(s) === "0") return "Pending";
    if (String(s) === "1") return "Approved";
    if (String(s) === "2") return "Rejected";
    return s || "-";
};

const StockAdjustmentsReport = () => {
    const [categories, setCategories] = useState([]);
    const [movementOptions, setMovementOptions] = useState([]);

    useEffect(() => {
        let mounted = true;
        api.get('/inventory/category/list.php?page=1&limit=100&active_only=1')
            .then((res) => {
                if (mounted) setCategories(res.data.dataset || []);
            })
            .catch(() => { /* Non-critical: filter dropdown stays empty */ });
        return () => { mounted = false; };
    }, []);

    return (
        <GenericReportPage
            title="Stock Adjustments Report"
            subtitle="Inventory stock movement adjustments with status and values"
            breadcrumb={["Reports", "Inventory"]}
            fetchUrl="/reports/inventory/stock_adjustments.php"
            rowKey="im_id"
            filterDefs={[
                { type: "search", key: "search", label: "Search", col: 4, placeholder: "Code, item, reason" },
                {
                    type: "select", key: "ic_id", label: "Category", col: 4,
                    options: categories.map((c) => ({ value: c.ic_id, label: c.ic_name })),
                    showSearch: true,
                },
                {
                    type: "select", key: "movement_type", label: "Movement", col: 3,
                    options: movementOptions.map((t) => ({ value: t, label: t })),
                    showSearch: true,
                },
                { type: "select", key: "status", label: "Status", col: 3, options: STATUS_OPTIONS },
                { type: "date", key: "dateRange", label: "Adjustment Date", col: 4 },
            ]}
            onData={(dataset) => {
                // Accumulate so the dropdown keeps all types seen so far (even after filtering)
                setMovementOptions((prev) =>
                    [...new Set([...prev, ...dataset.map((r) => r.movement_type).filter(Boolean)])].sort()
                );
            }}
            columns={[
                { title: 'Item Code', dataIndex: 'inv_code', key: 'inv_code', width: 110, sorter: (a, b) => (a.inv_code || '').localeCompare(b.inv_code || '') },
                { title: 'Item Name', dataIndex: 'item_name', key: 'item_name', sorter: (a, b) => (a.item_name || '').localeCompare(b.item_name || '') },
                { title: 'Category', dataIndex: 'category_name', key: 'category_name', render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Movement', dataIndex: 'movement_type', key: 'movement_type', width: 110 },
                { title: 'Qty', dataIndex: 'qty', key: 'qty', align: 'right', width: 80, sorter: (a, b) => (a.qty || 0) - (b.qty || 0), render: (v) => fmtQty(v) },
                { title: 'Unit Cost', dataIndex: 'unit_cost', key: 'unit_cost', align: 'right', width: 110, sorter: (a, b) => (a.unit_cost || 0) - (b.unit_cost || 0), render: (v) => fmtMoney(v) },
                { title: 'Total Cost', dataIndex: 'total_cost', key: 'total_cost', align: 'right', width: 120, sorter: (a, b) => (a.total_cost || 0) - (b.total_cost || 0), render: (v) => fmtMoney(v) },
                {
                    title: 'Status', dataIndex: 'status', key: 'status', width: 100,
                    render: (v) => {
                        const color = String(v) === "1" ? "#52c41a" : (String(v) === "2" ? "#ff4d4f" : "#faad14");
                        return <span style={{ color }}>{statusLabel(v)}</span>;
                    },
                },
                { title: 'Date', dataIndex: 'cr_dt', key: 'cr_dt', width: 110, render: (d) => dayjs(d).format("YYYY-MM-DD"), sorter: (a, b) => dayjs(a.cr_dt).valueOf() - dayjs(b.cr_dt).valueOf() },
                { title: 'Created By', dataIndex: 'creator_name', key: 'creator_name', width: 130 },
            ]}
            pdf={{
                title: "STOCK ADJUSTMENTS REPORT",
                subtitle: "Inventory stock movement adjustments",
                columns: [
                    { header: "Item Code" }, { header: "Item Name" }, { header: "Category" },
                    { header: "Movement" }, { header: "Qty", align: "right" },
                    { header: "Unit Cost", align: "right" }, { header: "Total Cost", align: "right" },
                    { header: "Status" }, { header: "Date" }, { header: "Created By" },
                ],
                rows: (rows) =>
                    rows.map((r) => [
                        r.inv_code || "-",
                        r.item_name || "-",
                        r.category_name || "—",
                        r.movement_type || "-",
                        fmtQty(r.qty),
                        fmtMoney(r.unit_cost),
                        fmtMoney(r.total_cost),
                        statusLabel(r.status),
                        dayjs(r.cr_dt).format("YYYY-MM-DD"),
                        r.creator_name || "-",
                    ]),
                columnStyles: { 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" } },
            }}
            excel={{
                filename: "stock-adjustments-report",
                sheetName: "Stock Adjustments",
                columns: [
                    { title: "Item Code", dataIndex: "inv_code", width: 14 },
                    { title: "Item Name", dataIndex: "item_name", width: 30 },
                    { title: "Category", dataIndex: "category_name", width: 20 },
                    { title: "Movement", dataIndex: "movement_type", width: 12 },
                    { title: "Qty", dataIndex: "qty", width: 10 },
                    { title: "Unit Cost", dataIndex: "unit_cost", width: 14 },
                    { title: "Total Cost", dataIndex: "total_cost", width: 14 },
                    { title: "Status", dataIndex: "status", width: 12 },
                    { title: "Date", dataIndex: "cr_dt", width: 14 },
                    { title: "Created By", dataIndex: "creator_name", width: 18 },
                ],
                rows: (rows) =>
                    rows.map((r) => ({
                        ...r,
                        status: statusLabel(r.status),
                        qty: fmtQty(r.qty),
                        unit_cost: fmtMoney(r.unit_cost),
                        total_cost: fmtMoney(r.total_cost),
                        cr_dt: dayjs(r.cr_dt).format("YYYY-MM-DD"),
                    })),
            }}
        />
    );
};

export default StockAdjustmentsReport;
